import React, { useEffect, useRef } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

import { useTranslation } from "react-i18next";
import Toast from "../../common/Toast/Toast";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  attachLocalImagePasteDropGuard,
  createBlockLocalImageUploadBefore,
  getEmailTemplateSunEditorOptions,
} from "../EmailTemplateEditor/emailTemplateSunEditorOptions";

const CustomizeTemplateStep = ({
  templateName,

  onEditorReady,
  toolbarOptions,
  onEditorChange,
  editorContent,
  onInsertVariable,
  onPrevious,
  onCreate,
  isCreating,
  isDataChanged,
}) => {
  const { t } = useTranslation();
  const editorContainerRef = useRef(null);
  const blockLocalImageUploadBefore = createBlockLocalImageUploadBefore({
    t,
    Toast,
  });

  useEffect(() => {
    const toolbarElement =
      editorContainerRef.current?.querySelector(".se-toolbar");
    if (!toolbarElement) return;

    const variableDropdown = document.createElement("select");
    variableDropdown.id = "clone-email-body-variable-select";
    variableDropdown.className = "clone-template-variables-select";
    variableDropdown.innerHTML = `
      <option value="">${t("settings:variables")}</option>
      <option value="first_name">${t("settings:first_name")}</option>
      <option value="last_name">${t("settings:last_name")}</option>
      <option value="org_name">${t("settings:organisation_name")}</option>
    `;
    variableDropdown.style.cssText =
      "width: 120px; margin-left: 6px; margin-bottom: 3px; padding: 5px 10px; font-size: 14px; border: 1px solid #D0D5DD; border-radius: 6px; background: white;";

    const handleVariableChange = (event) => {
      const value = event.target.value;
      if (value && onInsertVariable) {
        onInsertVariable(value);
        variableDropdown.value = "";
      }
    };

    variableDropdown.addEventListener("change", handleVariableChange);
    toolbarElement.appendChild(variableDropdown);

    return () => {
      variableDropdown.removeEventListener("change", handleVariableChange);
      if (toolbarElement.contains(variableDropdown)) {
        toolbarElement.removeChild(variableDropdown);
      }
    };
  }, [onInsertVariable, t]);

  useEffect(() => {
    let cleanup = () => {};
    const timer = setTimeout(() => {
      cleanup = attachLocalImagePasteDropGuard(editorContainerRef.current, {
        t,
        Toast,
      });
    }, 0);

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [editorContent, t]);

  useEffect(() => {
    const editable = editorContainerRef.current?.querySelector(
      ".sun-editor-editable",
    );
    if (editable) {
      editable.id = "clone-email-body-editor";
    }
  }, [editorContent]);

  return (
    <div className="px-20 py-10" id={pageRootTestIds.cloneEmailCustomizeStep}>
      <div className="bg-cfSecondary border border-cfIssuedScreenSearchBorder rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xl leading-tight font-semibold text-cfHeadingText">
              {t("settings:customize_template")}
            </p>
            <p className="text-cfGlobalText mt-2">
              {t("settings:edit")}:{" "}
              <span className="text-cfHeadingText font-semibold">
                {templateName}
              </span>
            </p>
          </div>
        </div>

        <div
          ref={editorContainerRef}
          className="mt-4 border border-cfIssuedScreenSearchBorder rounded-xl overflow-hidden"
        >
          <SunEditor
            getSunEditorInstance={onEditorReady}
            setOptions={{
              ...getEmailTemplateSunEditorOptions({ height: "60vh" }),
              buttonList: toolbarOptions,
            }}
            onImageUploadBefore={blockLocalImageUploadBefore}
            onChange={onEditorChange}
            value={editorContent}
          />
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            id="clone-email-customize-step-previous"
            onClick={onPrevious}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          >
            {t("settings:previous")}
          </button>

          <button
            type="button"
            id="clone-email-customize-step-create"
            onClick={onCreate}
            disabled={isCreating || !isDataChanged}
            className="flex items-center justify-center px-4 py-2  text-sm  capitalize transition-colors duration-200 rounded-md bg-cfPrimary text-cfSecondary font-semibold disabled:opacity-60 disabled:cursor-not-allowed hover:bg-cfPrimaryDark"
          >
            {isCreating
              ? t("settings:creating")
              : t("settings:create_from_template")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeTemplateStep;
